import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

type VesselSubType = string;

export type FilterColor =
  | 'error'
  | 'disabled'
  | 'success'
  | 'inherit'
  | 'action'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'warning'
  | undefined;

interface LengthFilter {
  minLength: number;
  maxLength: number;
}

export interface DimensionsFilter {
  length: LengthFilter;
}

interface ManuallySelectedFilter {
  selectedVessels: string[];
  deselectedVessels: string[];
}

export interface VesselTypesFilter {
  types: VesselSubType[];
}

interface HistoryFiltersState {
  manuallySelectedFilter: ManuallySelectedFilter;
  vesselTypesFilter: VesselTypesFilter;
  dimensionsFilter: DimensionsFilter;
  filterColor: FilterColor;
}

const defaultManuallySelectedFilter: ManuallySelectedFilter = {
  selectedVessels: [],
  deselectedVessels: [],
};

export const INITIAL_HISTORY_FILTERS_STATE: HistoryFiltersState = {
  manuallySelectedFilter: defaultManuallySelectedFilter,
  vesselTypesFilter: {
    types: [],
  },
  dimensionsFilter: {
    length: {
      minLength: 0,
      maxLength: 1000,
    },
  },
  filterColor: 'inherit',
};

const historyFilters = createSlice({
  name: 'historyFilters',
  initialState: INITIAL_HISTORY_FILTERS_STATE,
  reducers: {
    setVesselTypesFilter(state, action: PayloadAction<VesselTypesFilter>) {
      state.vesselTypesFilter = action.payload;
      // Updating any of the other filters resets the manual selection
      state.manuallySelectedFilter = defaultManuallySelectedFilter;
    },
    addVesselTypesFilters(state, action: PayloadAction<VesselSubType[]>) {
      state.vesselTypesFilter.types.push(...action.payload);
      // Updating any of the other filters resets the manual selection
      state.manuallySelectedFilter = defaultManuallySelectedFilter;
    },
    removeVesselTypesFilters(state, action: PayloadAction<VesselSubType[]>) {
      state.vesselTypesFilter.types = state.vesselTypesFilter.types.filter(
        (vt) => !action.payload.includes(vt)
      );
      // Updating any of the other filters resets the manual selection
      state.manuallySelectedFilter = defaultManuallySelectedFilter;
    },
    setDimensionsFilter(state, action: PayloadAction<DimensionsFilter>) {
      state.dimensionsFilter = action.payload;
      // Updating any of the other filters resets the manual selection
      state.manuallySelectedFilter = defaultManuallySelectedFilter;
    },
    addManuallySelectedFilter(state, action: PayloadAction<string>) {
      state.manuallySelectedFilter.selectedVessels.push(action.payload);
      const { deselectedVessels } = state.manuallySelectedFilter;
      state.manuallySelectedFilter.deselectedVessels = deselectedVessels.filter(
        (v) => v !== action.payload
      );
    },
    addManuallyDeselectedFilter(state, action: PayloadAction<string>) {
      state.manuallySelectedFilter.deselectedVessels.push(action.payload);
      const { selectedVessels } = state.manuallySelectedFilter;
      state.manuallySelectedFilter.selectedVessels = selectedVessels.filter(
        (v) => v !== action.payload
      );
    },
    setFilterColor(state, action: PayloadAction<FilterColor>) {
      state.filterColor = action.payload;
    },
  },
});

export const {
  setVesselTypesFilter,
  addVesselTypesFilters,
  removeVesselTypesFilters,
  setDimensionsFilter,
  addManuallyDeselectedFilter,
  addManuallySelectedFilter,
  setFilterColor,
} = historyFilters.actions;

export default historyFilters.reducer;
